import {
  getKidTemplateTypeRank,
  KidTemplateAffectedShareClass,
  KidTemplateIncompleteEntryTemplate,
  KidTemplateOutputModel,
  KidTemplateType,
} from '../../../../api/kids/types';
import { RaidrV2RoutePaths } from '../../../../routes/page-routes';
import {
  buildTemplatePdfElements,
  SelectableKidTemplate,
} from '../../raidr/kidsV2/components/form/templateSelection.utils';
import type {
  FormatOptions,
  PdfElement,
} from '../../raidr/kidsV2/types/KidPdfGeneratorV2.types';
import {
  formatKidTemplateTypeLabel,
  KID_TEMPLATE_TYPE_VALUE_SET,
  UNKNOWN_TEMPLATE_GROUP_KEY,
} from './templateManagement.constants';
import type {
  FundOption,
  TemplateGroup,
  TemplateListTableRow,
  TemplateReferenceOptions,
  TemplateRow,
  TemplateSubgroup,
} from './templateManagement.types';

const UNASSIGNED_SUBGROUP_KEY = '__unassigned__';

/** Short and long fund names from the funds API (`fund_name`, `fund_name_full`). */
export const formatFundOptionLabel = (fund: FundOption): string => {
  const shortName = fund.fund_name?.trim();
  const longName = fund.fund_name_full?.trim();

  if (shortName && longName && shortName !== longName) {
    return `${shortName} — ${longName}`;
  }

  return longName || shortName || fund.fund_id;
};

export const templateTypeUsesReferenceSubgroups = (typeKey: string): boolean =>
  typeKey !== KidTemplateType.MANCO && typeKey !== UNKNOWN_TEMPLATE_GROUP_KEY;

export const resolveTemplateReferenceLabel = (
  templateType: KidTemplateType | string,
  referenceId: string | null | undefined,
  options: TemplateReferenceOptions,
): string => {
  const id = referenceId?.trim();
  if (!id) return 'Unassigned';

  switch (templateType) {
    case KidTemplateType.SUB_FUND: {
      const fund = options.availableFunds.find((entry) => entry.fund_id === id);
      return fund ? formatFundOptionLabel(fund) : id;
    }
    case KidTemplateType.FUND_MANAGER: {
      const fundManager = options.availableFundManagers.find(
        (entry) => entry.fund_manager_id === id,
      );
      return fundManager?.fund_manager_name || id;
    }
    case KidTemplateType.CIU_FUND: {
      const ciu = options.availableCIUs.find((entry) => entry.ciu_id === id);
      return ciu?.ciu_name || id;
    }
    case KidTemplateType.ADMINISTRATOR: {
      const administrator = options.availableAdministrators.find(
        (entry) => entry.administrator_id === id,
      );
      return administrator?.administrator_name || id;
    }
    default:
      return id;
  }
};

const sortTemplateListRowsByName = (
  items: TemplateListTableRow[],
): TemplateListTableRow[] =>
  [...items].sort((a, b) => {
    const appliesDiff = a.appliesToLabel.localeCompare(b.appliesToLabel, undefined, {
      sensitivity: 'base',
    });
    if (appliesDiff !== 0) return appliesDiff;
    return (a.template.template_name || a.template.template_id).localeCompare(
      b.template.template_name || b.template.template_id,
      undefined,
      { sensitivity: 'base' },
    );
  });

export const buildTemplateSubgroups = (
  typeKey: string,
  items: TemplateRow[],
  options: TemplateReferenceOptions,
): TemplateSubgroup[] | undefined => {
  if (!templateTypeUsesReferenceSubgroups(typeKey)) return undefined;

  const byReference = new Map<string, TemplateListTableRow[]>();
  items.forEach((row) => {
    const referenceKey =
      row.template.reference_id?.trim() || UNASSIGNED_SUBGROUP_KEY;
    const listRow: TemplateListTableRow = {
      ...row,
      typeKey,
      appliesToLabel:
        typeKey === KidTemplateType.MANCO
          ? 'All documents'
          : resolveTemplateReferenceLabel(
              typeKey,
              row.template.reference_id,
              options,
            ),
    };
    if (!byReference.has(referenceKey)) byReference.set(referenceKey, []);
    byReference.get(referenceKey)!.push(listRow);
  });

  const subgroups = [...byReference.entries()].map(
    ([referenceKey, subgroupItems]) => ({
      key: referenceKey,
      label:
        referenceKey === UNASSIGNED_SUBGROUP_KEY
          ? 'Unassigned'
          : resolveTemplateReferenceLabel(typeKey, referenceKey, options),
      items: sortTemplateListRowsByName(subgroupItems),
    }),
  );

  subgroups.sort((left, right) => {
    if (left.key === UNASSIGNED_SUBGROUP_KEY) return 1;
    if (right.key === UNASSIGNED_SUBGROUP_KEY) return -1;
    return left.label.localeCompare(right.label, undefined, {
      sensitivity: 'base',
    });
  });

  return subgroups;
};

/** Group overview rows by template type, then by scope reference. */
export const buildTemplateListGroups = (
  rows: TemplateListTableRow[],
): TemplateGroup[] => {
  const byType = new Map<string, TemplateListTableRow[]>();
  rows.forEach((row) => {
    if (!byType.has(row.typeKey)) byType.set(row.typeKey, []);
    byType.get(row.typeKey)!.push(row);
  });

  const typeKeys = [...byType.keys()].sort((a, b) => {
    const rankA = KID_TEMPLATE_TYPE_VALUE_SET.has(a)
      ? getKidTemplateTypeRank(a as KidTemplateType)
      : -1;
    const rankB = KID_TEMPLATE_TYPE_VALUE_SET.has(b)
      ? getKidTemplateTypeRank(b as KidTemplateType)
      : -1;
    if (rankB !== rankA) return rankB - rankA;
    return formatKidTemplateTypeLabel(a).localeCompare(
      formatKidTemplateTypeLabel(b),
      undefined,
      { sensitivity: 'base' },
    );
  });

  return typeKeys.map((typeKey) => {
    const items = sortTemplateListRowsByName(byType.get(typeKey)!);

    if (!templateTypeUsesReferenceSubgroups(typeKey)) {
      return {
        typeKey,
        label: formatKidTemplateTypeLabel(typeKey),
        items,
      };
    }

    const byReference = new Map<string, TemplateListTableRow[]>();
    items.forEach((row) => {
      const referenceKey =
        row.template.reference_id?.trim() || UNASSIGNED_SUBGROUP_KEY;
      if (!byReference.has(referenceKey)) byReference.set(referenceKey, []);
      byReference.get(referenceKey)!.push(row);
    });

    const subgroups: TemplateSubgroup[] = [...byReference.entries()].map(
      ([referenceKey, subgroupItems]) => ({
        key: referenceKey,
        label:
          referenceKey === UNASSIGNED_SUBGROUP_KEY
            ? 'Unassigned'
            : subgroupItems[0]?.appliesToLabel || referenceKey,
        items: sortTemplateListRowsByName(subgroupItems),
      }),
    );

    subgroups.sort((left, right) => {
      if (left.key === UNASSIGNED_SUBGROUP_KEY) return 1;
      if (right.key === UNASSIGNED_SUBGROUP_KEY) return -1;
      return left.label.localeCompare(right.label, undefined, {
        sensitivity: 'base',
      });
    });

    return {
      typeKey,
      label: formatKidTemplateTypeLabel(typeKey),
      items,
      subgroups,
    };
  });
};

export const countTemplatesWithDocumentIssues = (
  items: Pick<
    TemplateListTableRow,
    'missingDocumentCount' | 'invalidDocumentCount' | 'documentCount'
  >[],
): number =>
  items.filter(
    (item) =>
      item.missingDocumentCount > 0 ||
      item.invalidDocumentCount > 0 ||
      item.documentCount > 0,
  ).length;

export const templateListSubgroupCollapseKey = (
  typeKey: string,
  subgroupKey: string,
): string => `${typeKey}::${subgroupKey}`;

export const buildKidEditorV2Url = (
  row: Pick<
    KidTemplateAffectedShareClass,
    'fund_id' | 'share_class_id' | 'document_language'
  >,
): string =>
  `${RaidrV2RoutePaths.KID_EDITOR_V2}?fundId=${row.fund_id}&shareClassId=${row.share_class_id}&documentLanguage=${encodeURIComponent(row.document_language)}`;

export const normalizeTemplateFormatOptions = (
  value: unknown,
): Record<string, unknown> | null | undefined => {
  if (value == null) return value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      const parsed: unknown = JSON.parse(trimmed);
      return typeof parsed === 'object' &&
        parsed !== null &&
        !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return null;
};

export const toSelectableKidTemplate = (
  template: KidTemplateOutputModel | KidTemplateIncompleteEntryTemplate,
): SelectableKidTemplate => {
  const renderedContent =
    ('rendered_content' in template &&
    typeof template.rendered_content === 'string'
      ? template.rendered_content
      : null) ||
    ('template_rendered_content' in template &&
    typeof template.template_rendered_content === 'string'
      ? template.template_rendered_content
      : null) ||
    template.template_content;

  return {
    template_id: template.template_id,
    template_name: template.template_name,
    template_type: template.template_type as KidTemplateType,
    template_content: template.template_content,
    reference_id: template.reference_id ?? null,
    rendered_content: renderedContent,
    format_options: normalizeTemplateFormatOptions(
      'format_options' in template ? template.format_options : null,
    ),
  };
};

/** Empty editor state — only user-applied styling is persisted. */
export const getInitialTemplateEditorFormatOptions = (): FormatOptions => ({});

export const formatOptionsForPersistence = (
  options: FormatOptions,
): Record<string, unknown> | null => {
  const entries = Object.entries(options).filter(
    ([, value]) => value !== undefined && value !== null && value !== '',
  );
  return entries.length ? Object.fromEntries(entries) : null;
};

export const toTemplateEditorFormatOptions = (value: unknown): FormatOptions => {
  const normalized = normalizeTemplateFormatOptions(value);
  return normalized ? (normalized as FormatOptions) : {};
};

/** Substitute in-progress template edits into a document's element list for preview. */
export const applyEditedTemplateToDocumentElements = (
  elements: PdfElement[],
  templateId: string,
  content: string,
  formatOptions: FormatOptions,
): { elements: PdfElement[]; templateFound: boolean } => {
  const persistedFormatOptions = formatOptionsForPersistence(formatOptions);
  const layout = formatOptions?.layout;
  let cleanedFormatOptions: PdfElement['format_options'] = persistedFormatOptions as
    | PdfElement['format_options']
    | undefined;

  if (persistedFormatOptions && layout) {
    const { layout: _layout, ...rest } = persistedFormatOptions;
    cleanedFormatOptions =
      Object.keys(rest).length > 0
        ? (rest as PdfElement['format_options'])
        : undefined;
  }

  let templateFound = false;
  const updated = elements.map((element) => {
    if (element.template_id !== templateId) return element;
    templateFound = true;
    return {
      ...element,
      content,
      rendered_content: content,
      format_options: cleanedFormatOptions,
      layout: layout ?? undefined,
    };
  });

  if (templateFound) {
    return { elements: updated, templateFound: true };
  }

  const previewTemplate: SelectableKidTemplate = {
    template_id: templateId,
    template_name: 'Template',
    template_type: KidTemplateType.MANCO,
    template_content: content,
    reference_id: null,
    format_options: persistedFormatOptions,
    rendered_content: content,
  };

  return {
    elements: [
      ...updated,
      ...buildTemplatePdfElements([previewTemplate]),
    ],
    templateFound: false,
  };
};
